/**
 * Top-level domains a scheme-less, `www.`-less word is accepted as a link for. A bare
 * `Sale.Monday` must stay prose, so anything outside this list needs an explicit scheme or `www.`.
 */
const BARE_DOMAIN_TLDS = [
  "ai",
  "app",
  "beauty",
  "biz",
  "ca",
  "clinic",
  "co",
  "com",
  "de",
  "dev",
  "fr",
  "health",
  "info",
  "io",
  "link",
  "ly",
  "md",
  "me",
  "mx",
  "net",
  "online",
  "org",
  "page",
  "shop",
  "site",
  "spa",
  "store",
  "uk",
  "us",
  "xyz",
];

const NOT_AFTER_WORD = String.raw`(?<![\w@./-])`;
const URL_TAIL = String.raw`[^\s<>"']*`;

const LINK_PATTERN = new RegExp(
  [
    String.raw`\bhttps?:\/\/${URL_TAIL}`,
    String.raw`${NOT_AFTER_WORD}www\.${URL_TAIL}`,
    String.raw`\btel:\+?[\d\-().]{3,}`,
    String.raw`\bmailto:[^\s<>"',;]+`,
    String.raw`${NOT_AFTER_WORD}(?:[a-z0-9-]+\.)+(?:${BARE_DOMAIN_TLDS.join("|")})\b(?::\d+)?(?:[/?#]${URL_TAIL})?`,
    String.raw`(?<![^\s(])\/[A-Za-z0-9]${URL_TAIL}`,
  ].join("|"),
  "gi",
);

const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/;
const CLOSERS: Record<string, string> = { ")": "(", "]": "[" };

/** Strips sentence punctuation and any closing bracket the link did not open. */
const trimTrailing = (link: string): string => {
  let trimmed = link.replace(TRAILING_PUNCTUATION, "");

  for (;;) {
    const closer = trimmed.at(-1) ?? "";
    const opener = CLOSERS[closer];

    if (!opener || countOf(trimmed, closer) <= countOf(trimmed, opener)) {
      return trimmed;
    }

    trimmed = trimmed.slice(0, -1).replace(TRAILING_PUNCTUATION, "");
  }
};

const countOf = (text: string, character: string): number => text.split(character).length - 1;

/**
 * The links in a message, in the order they appear, each once. Returns the text exactly as the
 * author wrote it (no scheme added) so callers can match it back to the message; classifying it is
 * `classifyLink`'s job. Email addresses are not links.
 */
export const extractMessageLinks = (text: string): string[] => {
  const links: string[] = [];

  for (const match of text.matchAll(LINK_PATTERN)) {
    const link = trimTrailing(match[0]);
    const followedByAt = text[match.index + match[0].length] === "@";

    if (link && !followedByAt && !links.includes(link)) {
      links.push(link);
    }
  }

  return links;
};
